import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export interface SearchOption {
  readonly id: string;
  readonly label: string;
  /** Second line: CUIT, condición IVA… */
  readonly detail?: string | undefined;
}

/** Name or document, case- and accent-insensitive; a typed CUIT matches with or without dashes. */
export function matches(option: SearchOption, query: string): boolean {
  const q = fold(query.trim());
  if (!q) return true;
  const text = fold(`${option.label} ${option.detail ?? ""}`);
  const digits = query.replace(/\D/g, "");
  return text.includes(q) || (digits.length >= 3 && text.replace(/\D/g, "").includes(digits));
}

function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/**
 * A text box that searches a list as you type (companies, clients, suppliers). Arrow keys move,
 * Enter picks, Escape closes. With `onCreate`, typing something not on the list offers it as new.
 */
export function SearchSelect({
  options,
  value,
  onChange,
  onPick,
  placeholder,
  ariaLabel,
  emptyText = "No hay coincidencias.",
  footer,
  className = "",
}: {
  options: readonly SearchOption[];
  /** The text in the box (controlled). */
  value: string;
  onChange: (text: string) => void;
  onPick: (option: SearchOption) => void;
  placeholder: string;
  ariaLabel?: string;
  emptyText?: string;
  footer?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const shown = options.filter((o) => matches(o, value)).slice(0, 50);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => setActive(0), [value]);

  const pick = (option: SearchOption) => {
    onPick(option);
    setOpen(false);
  };

  return (
    <div ref={box} className={`relative ${className}`}>
      <input
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel ?? placeholder}
        className="w-full rounded-md border border-brand-line bg-white px-2.5 py-1.5 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-tint"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, shown.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter" && open && shown[active]) {
            e.preventDefault();
            pick(shown[active]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-40 mt-1 max-h-72 w-full min-w-64 overflow-auto rounded-md border border-brand-line bg-white py-1 text-sm shadow-lg"
        >
          {shown.map((option, index) => (
            <li
              key={option.id}
              role="option"
              aria-selected={index === active}
              className={`cursor-pointer px-3 py-1.5 ${index === active ? "bg-brand-tint" : ""}`}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(option);
              }}
            >
              <span className="block">{option.label}</span>
              {option.detail ? (
                <span className="block text-xs text-brand-muted">{option.detail}</span>
              ) : null}
            </li>
          ))}
          {shown.length === 0 ? (
            <li className="px-3 py-1.5 text-brand-muted">{emptyText}</li>
          ) : null}
          {footer ? <li className="border-t border-brand-line px-3 py-1.5">{footer}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}
